/**
 * Signing v2, phase B (Luis + Jaron meeting 2026-10-05; tickets 13213629138,
 * 13213613154, 13019071670, 13109586595): new signatures on the Job Summary
 * (monthly payment + monitoring choice), on the NOTES pages and on the M+S /
 * CE Financing Information, and "Confirm Equipment" out of the guided steps.
 *
 * A signed document never changes on its own (V67), so all of that renders
 * only for a customer marked `signing_flow_version === 'v2'` or one with no
 * signature at all. Everyone who signed before keeps layout, pagination,
 * guided steps and Finish exactly as they were.
 */
import { coverFollowsContract } from './coverLogo'
import { calcLoanMonthlyPay } from './helpers'

export const SIGNING_FLOW_V2 = 'v2'

/** $/mo, display-only (V46). Same figure as the Job Summary's own constant. */
export const MONITORING_MONTHLY = 9.99

export type MonitoringChoice = '' | 'with' | 'self'

/**
 * Every signature a customer record can hold. Broad on purpose: any one of
 * them makes the customer "already signed", which keeps the old layout.
 */
export const SIGNATURE_FIELDS = [
  'c_signature',
  'r_signature',
  'report_company_signature',
  'security_contract_sign_customer',
  'security_contract_sign_company',
  'security_contract_cancel_sign_customer',
  'security_completion_sign_customer',
  'home_improvement_sign_owner1',
  'home_improvement_sign_owner2',
  'home_improvement_sign_company',
  'home_improvement_cancel_sign_owner1',
  'home_improvement_cancel_sign_owner2',
  'home_improvement_confirm_equipment_sign',
  'home_improvement_confirm_total_initials',
  'home_improvement_financing_info_sign',
  'hil_completion_client_sign',
  'roof_claiming_client_sign',
  'roof_claiming_company_rep_sign',
  'roof_claiming_completion_client_sign',
  'job_summary_payment_sign',
  'job_summary_monthly_sign',
  'job_summary_notes_sign',
] as const

/** Signatures that live on the report (Job Summary / NOTES), not the contract. */
export const PHASE1_SIGNATURE_FIELDS = [
  'job_summary_payment_sign',
  'job_summary_monthly_sign',
  'job_summary_notes_sign',
] as const

type CustomerLike = Readonly<Record<string, unknown>> | null | undefined

const filled = (value: unknown): boolean =>
  typeof value === 'string' && !!value.trim()

export const hasAnySignature = (customer: CustomerLike): boolean =>
  !!customer && SIGNATURE_FIELDS.some((field) => filled(customer[field]))

/**
 * For the cover only: `hasAnySignature` plus what it leaves out on purpose. The
 * third-party authorization is a signature too, and a backup means a signature
 * existed and was replaced or deleted. Either way the cover stays put (V67).
 */
export const coverHasSignature = (customer: CustomerLike): boolean =>
  hasAnySignature(customer) ||
  (!!customer &&
    ['roof_third_party_sign', 'c_signature_backup'].some((field) =>
      filled(customer[field]),
    ))

/**
 * Written with the customer's first signature. A cover following the contract
 * (`coverFollowsContract`) applies only while nothing is signed, so without
 * this the very signature made under the Olson logo would turn the cover back
 * to HomeCam. Storing the company as the cover pick keeps what was signed on
 * (V67), same idea as `signingFlowStamp` and the Roof Claim snapshot (V100).
 * Empty for anyone already signed: their cover is not touched.
 */
export const coverFirstSignatureStamp = (
  customer: CustomerLike,
): { coverCompany?: string } => {
  if (!customer) return {}
  const shortName = customer['contractCompany']
  const coverCompany = customer['coverCompany']
  return typeof shortName === 'string' &&
    coverFollowsContract({
      coverCompany: typeof coverCompany === 'string' ? coverCompany : '',
      shortName,
      signed: coverHasSignature(customer),
    })
    ? { coverCompany: shortName }
    : {}
}

/**
 * True when the v2 blocks (new signatures, monitoring choice) and v2 guided
 * steps apply. An unloaded customer (null/undefined) is never v2.
 */
export const isSigningFlowV2 = (customer: CustomerLike): boolean =>
  !!customer &&
  (customer['signing_flow_version'] === SIGNING_FLOW_V2 ||
    !hasAnySignature(customer))

/**
 * What to write alongside a signature (or on guided Start) so the customer
 * stays on v2 once they are no longer unsigned. Empty for a customer already
 * marked, and for one who signed under the old layout (never re-classified).
 */
export const signingFlowStamp = (
  customer: CustomerLike,
): { signing_flow_version?: string } =>
  customer &&
  customer['signing_flow_version'] !== SIGNING_FLOW_V2 &&
  isSigningFlowV2(customer)
    ? { signing_flow_version: SIGNING_FLOW_V2 }
    : {}

export const hasPhase1Signature = (customer: CustomerLike): boolean =>
  !!customer && PHASE1_SIGNATURE_FIELDS.some((field) => filled(customer[field]))

/**
 * When M+S started offering the monitoring choice (Jaron, Oct 14 2026,
 * Job Summary screenshot: "We need to choose? Sign here! I already asked
 * about this..."; Luis approved). Before it, M+S never offered it.
 */
export const MSE_MONITORING_CHOICE_SINCE = Date.UTC(2026, 9, 14, 21, 0)

/**
 * Caballero's never shows the monitoring choice (alarm-safe, Maria G).
 * M+S does since Oct 14, except a Job Summary whose payment was signed before
 * that: it keeps the layout and figure it was signed with (V67).
 */
export const monitoringChoiceOffered = (
  company: string | undefined,
  customer?: CustomerLike,
): boolean => {
  if (company === 'CE') return false
  if (company !== 'MSE') return true
  const signedAt = customer?.['job_summary_payment_sign_date']
  return !(
    filled(customer?.['job_summary_payment_sign']) &&
    !(typeof signedAt === 'number' && signedAt >= MSE_MONITORING_CHOICE_SINCE)
  )
}

export const sanitizeMonitoringChoice = (value: unknown): MonitoringChoice =>
  value === 'with' || value === 'self' ? value : ''

/**
 * Monthly figure the Job Summary shows and the payment signature confirms:
 * the applied loan's monthly payment, plus monitoring only when the customer
 * chose "with". Never touches any TOTAL (V46).
 */
export const jobSummaryMonthly = (
  monthlyPayment: number,
  choice: MonitoringChoice,
  offered: boolean,
): number =>
  Math.round(
    (monthlyPayment + (offered && choice === 'with' ? MONITORING_MONTHLY : 0)) *
      100,
  ) / 100

/**
 * Inverse of `jobSummaryMonthly` for a signed Job Summary: the loan payment
 * without monitoring, from the frozen amount. Monitoring is only taken off
 * where it was added (offered and "with"): M+S / CE never add it, even when a
 * "with" was saved before the rep picked the company.
 */
export const loanMonthlyFromSigned = (
  signedAmount: number,
  choice: MonitoringChoice,
  offered: boolean,
): number =>
  Math.round(
    (signedAmount - (offered && choice === 'with' ? MONITORING_MONTHLY : 0)) *
      100,
  ) / 100

/**
 * The applied loan's monthly payment — same formula as the Job Summary and
 * the contract templates (V51): 0 for cash or a term of 0 years.
 */
export const appliedMonthlyPayment = (
  loan: {
    grandTotalWDF: number
    paymentMethod: string
    termInterest: number
    termYears: number
  } | null,
): number => {
  if (!loan) return 0
  const { grandTotalWDF, paymentMethod, termInterest, termYears } = loan
  if (paymentMethod === 'cash' || termYears <= 0) return 0
  if (termInterest === 0) return grandTotalWDF / termYears / 12
  return calcLoanMonthlyPay(grandTotalWDF, termInterest * 100, termYears)
}

/**
 * Jaron, Oct 14 2026 (13290658389, "I need the finance to match the rates
 * exactly", third time asked): we do not have the lenders' rate tables, so
 * the rep types the exact monthly the lender quotes. That typed amount lives
 * in `home_improvement_monthly_payment` (the M+S / CE contract's Monthly box,
 * same field the Job Summary edits) and wins over our own calculation.
 * Anything that is not a positive amount means "not typed" and falls back to
 * `computed`.
 */
export const parseMonthlyInput = (value: unknown): number => {
  const n = parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''))
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0
}

export const effectiveMonthlyPayment = (
  manual: unknown,
  computed: number,
): number => parseMonthlyInput(manual) || computed

/**
 * Written with the Job Summary payment signature: when the rep never typed a
 * monthly, the calculated one the customer just signed is stored in the
 * contract's Monthly box, so the agreement signed after it shows the same
 * amount and keeps it (V67). A typed amount is never overwritten.
 */
export const monthlyPersistExtra = (
  customer: CustomerLike,
  monthlyPayment: number,
): { home_improvement_monthly_payment?: string } =>
  parseMonthlyInput(customer?.['home_improvement_monthly_payment']) > 0 ||
  !(monthlyPayment > 0)
    ? {}
    : { home_improvement_monthly_payment: monthlyPayment.toFixed(2) }

/** The payment signature can be placed: a choice exists where one is offered. */
export const paymentSignReady = (
  choice: MonitoringChoice,
  offered: boolean,
): boolean => !offered || choice !== ''

/**
 * Written in the same update as `job_summary_payment_sign`: the amount the
 * customer signed for, frozen (V67), plus the v2 stamp.
 */
export const paymentSignatureExtras = ({
  company,
  customer,
  monthlyPayment,
}: {
  company: string | undefined
  customer: CustomerLike
  monthlyPayment: number
}): {
  job_summary_payment_signed_amount: number
  signing_flow_version?: string
} => ({
  ...signingFlowStamp(customer),
  job_summary_payment_signed_amount: jobSummaryMonthly(
    monthlyPayment,
    sanitizeMonitoringChoice(customer?.['job_summary_monitoring_choice']),
    monitoringChoiceOffered(company, customer),
  ),
})

export type MonitoringOptionCopy = Readonly<{
  value: 'with' | 'self'
  label: string
  /** The final cost of this option, right-aligned under the Total. */
  final: string
  /** How the final cost is made up, so the customer never adds it up. */
  breakdown: string
}>

/**
 * Jaron, Oct 25 2026 (13386542071): "do the one we had before where it
 * explains monitoring and shows the final cost. They can choose self
 * monitoring. It shows the cost and monitoring with $9.99 added." The design
 * he means is the Aug 26 comparison (358a8ead: "$70.00 + $9.99 = $79.99"
 * against "$70.00 + $0.00"), which v2 first kept (354064ab) and the Oct 8
 * change (f6551992) reduced to "Monitoring: $9.99" / "$0.00".
 *
 * `loanMonthly` > 0 (financing): each option shows its final monthly, the
 * loan payment plus that option's monitoring. Otherwise (cash): the total is
 * paid once, "With monitoring" adds $9.99/mo on top, "Self monitoring" has
 * no monthly fee. Display only, never enters any TOTAL (V46).
 */
export const monitoringOptionCopy = ({
  loanMonthly,
  total,
  totalLabel,
  money,
}: {
  loanMonthly: number
  total: number
  /** Printed instead of the total: "N/A" until Apply (13443992736). */
  totalLabel?: string | undefined
  money: (n: number) => string
}): readonly [MonitoringOptionCopy, MonitoringOptionCopy] => {
  const fee = money(MONITORING_MONTHLY)
  if (loanMonthly > 0) {
    const loan = money(loanMonthly)
    const withMonthly = money(jobSummaryMonthly(loanMonthly, 'with', true))
    return [
      {
        value: 'with',
        label: 'With monitoring',
        final: `${withMonthly}/mo`,
        breakdown: `Monthly payment ${loan} + Monitoring ${fee} = ${withMonthly}/mo`,
      },
      {
        value: 'self',
        label: 'Self monitoring',
        final: `${loan}/mo`,
        breakdown: `Monthly payment ${loan} + Monitoring ${money(
          0,
        )} = ${loan}/mo`,
      },
    ]
  }
  const cost = totalLabel ?? money(total)
  return [
    {
      value: 'with',
      label: 'With monitoring',
      final: `${cost} + ${fee}/mo`,
      breakdown: `Total ${cost} + Monitoring ${fee}/mo, billed monthly`,
    },
    {
      value: 'self',
      label: 'Self monitoring',
      final: cost,
      breakdown: `Total ${cost}, no monthly monitoring fee`,
    },
  ]
}
